import React, { useEffect, useState } from "react";
import AdminAppBar from "../../adminComponents/AdminAppBar";
import Label from "../../Form/Label";
import Heading from "../../Form/Heading";
import { MdOutlineSettings } from "react-icons/md";
import { useLocation, useNavigate } from "react-router-dom";
import GLPagesService from "../../Services/GLPagesService";
import SpinWheelService from "../../Services/SpinWheelService";
import { ToastContainer } from "react-toastify";
import {
  showErrorToast,
  showInfoToast,
  showSuccessToast,
} from "../../Constant/Notification";
import Select from "react-select";
import BackArrows from "../../Form/BackArrow/BackArrows";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { getIcons } from "../../Constant/Constant";

const GLpagesCreateTemplate = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { GLPageId } = location.state || {};
  const [showPreview, setShowPreview] = useState(false);
  const [loading, setLoading] = useState(false);
  const [linkErrors, setLinkErrors] = useState([]);
  const [loadingState, setLoadingState] = useState({
    logo: false,
    banner: false,
    footerLogo: false,
  });

  const icons = getIcons();
  const platformPatterns = {
    facebook: /^https?:\/\/(www\.)?facebook\.com\/.+$/,
    instagram: /^https?:\/\/(www\.)?instagram\.com\/.+$/,
    twitter: /^https?:\/\/(www\.)?twitter\.com\/.+$/,
    youtube: /^https?:\/\/(www\.)?youtube\.com\/.+$/,
    linkedin: /^https?:\/\/(www\.)?linkedin\.com\/.+$/,
  };

  const sectionToTypeMap = {
    topSellingProperties: "TopSelling",
    plotProperties: "Layout",
    villaProperties: "Villa",
    apartmentProperties: "Apartment",
  };

  const steps = [
    { id: 1, label: "Header" },
    { id: 2, label: "Property Sections" },
    { id: 3, label: "Footer & Contact" },
  ];

  const [currentStep, setCurrentStep] = useState(1);

  const [sectionProperties, setSectionProperties] = useState({
    topSellingProperties: [],
    plotProperties: [],
    villaProperties: [],
    apartmentProperties: [],
    resaleBuildingProperties: [],
    resaleLandProperties: [],
    acresAndCentsProperties: [],
  });

  const initialFormData = {
    landingPageId: "",
    header: {
      logo: "",
      image: "",
      description: "",
    },
    topSellingProperties: {
      enable: false,
      propertyPages: [],
    },
    plotProperties: {
      enable: false,
      propertyPages: [],
    },
    villaProperties: {
      enable: false,
      propertyPages: [],
    },
    apartmentProperties: {
      enable: false,
      propertyPages: [],
    },
    resaleBuildingProperties: {
      enable: false,
      propertyPages: [],
    },
    resaleLandProperties: {
      enable: false,
      propertyPages: [],
    },
    acresAndCentsProperties: {
      enable: false,
      propertyPages: [],
    },
    footer: {
      logo: "",
      facebook: "",
      instagram: "",
      twitter: "",
      youtube: "",
      linkedin: "",
    },
    contactUs: {
      address: "",
      email: "",
      phoneNumber: "",
    },
  };

  const [formData, setFormData] = useState(initialFormData);
  const [selectedProperties, setSelectedProperties] = useState({
    topSellingProperties: [],
    plotProperties: [],
    villaProperties: [],
    apartmentProperties: [],
    resaleBuildingProperties: [],
    resaleLandProperties: [],
    acresAndCentsProperties: [],
  });

  const imageSampleGuide = {
    "GL Listing page": {
      headerAndHero: icons.GLImage1,
      topSellingProperties: icons.GLImage2,
      plotProperties: icons.GLImage3,
      villaProperties: icons.GLImage4,
      apartmentProperties: icons.GLImage5,
      footerLogo: icons.GLImage6,
    },
  };

  useEffect(() => {
    const fetchGLPageData = async () => {
      try {
        setLoading(true);
        const response = await GLPagesService.getLandingGLpage();
        if (
          response.status === 200 &&
          response.data &&
          response.data.length > 0
        ) {
          const data = response.data[0];
          setFormData(data);

          const newSelectedProps = { ...selectedProperties };

          for (const section of Object.keys(sectionToTypeMap)) {
            if (
              data[section]?.enable &&
              data[section]?.propertyPages?.length > 0
            ) {
              await fetchPropertiesForSection(section);

              newSelectedProps[section] = data[section].propertyPages.map(
                (prop) => ({
                  value: prop.glPageId,
                  label: prop.name,
                  id: prop.id,
                  order: prop.order,
                })
              );
            }
          }

          setSelectedProperties(newSelectedProps);
        }
      } catch (error) {
        console.error("Error loading GL page:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchGLPageData();
  }, []);

  const fetchPropertiesForSection = async (section) => {
    try {
      const type = sectionToTypeMap[section];
      const propertiesResponse = await GLPagesService.getGLpageProperyName(
        type
      );

      if (propertiesResponse?.data) {
        const options = propertiesResponse.data.map((p) => ({
          value: p.id,
          label: p.name,
          glPageId: p.id,
        }));

        setSectionProperties((prev) => ({
          ...prev,
          [section]: options,
        }));
      }
    } catch (error) {
      showErrorToast(`Error loading properties for ${section}`);
      console.error(`Error loading properties for ${section}:`, error);
    }
  };

  const handleSectionToggle = async (section, enabled) => {
    setFormData((prev) => {
      const prevSection = prev[section] ?? { enable: false, propertyPages: [] };
      return {
        ...prev,
        [section]: {
          ...prevSection,
          enable: enabled,
          propertyPages: enabled ? prevSection.propertyPages : [],
        },
      };
    });

    if (
      enabled &&
      (!sectionProperties[section] || sectionProperties[section].length === 0)
    ) {
      try {
        await fetchPropertiesForSection(section);
      } catch (error) {
        console.error(`Error fetching properties for ${section}:`, error);
      }
    }
  };

  const handleImageUpload = async (file, type) => {
    try {
      setLoadingState((prev) => ({ ...prev, [type]: true }));
      const formData = new FormData();
      formData.append("file", file);
      const response = await SpinWheelService.createBannerImageTemplate(
        formData,
        "Landing Gl Page"
      );

      if (response?.data?.imageUrl) {
        return response.data.imageUrl;
      }
      throw new Error("Failed to get image URL");
    } catch (error) {
      showErrorToast("Image upload failed");
      console.error("Image upload error:", error);
      return null;
    } finally {
      setLoadingState((prev) => ({ ...prev, [type]: false }));
    }
  };

  const handleImageChange = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = [
      "image/png",
      "image/svg+xml",
      "image/webp",
      "image/jpeg",
      "image/jpg",
    ];

    if (!allowedTypes.includes(file.type)) {
      showErrorToast("Only PNG, SVG, WebP, JPG or JPEG images are allowed.");
      return;
    }

    const maxSizeMB = 1;
    if (file.size > maxSizeMB * 1024 * 1024) {
      showErrorToast("Image must be less than 1MB.");
      return;
    }

    const validDimensions = {
      logo: { width: 100, height: 35 },
      banner: { width: 1300, height: 700 },
      footerLogo: { width: 100, height: 35 },
    };

    const expectedSize = validDimensions[type];
    if (!expectedSize) {
      showErrorToast("Invalid image type selected.");
      return;
    }

    const image = new Image();
    const objectUrl = URL.createObjectURL(file);
    image.src = objectUrl;

    image.onload = async () => {
      const { width, height } = image;
      URL.revokeObjectURL(objectUrl);

      const tolerance = 10;

      const isWidthInRange =
        width >= expectedSize.width - tolerance &&
        width <= expectedSize.width + tolerance;
      const isHeightInRange =
        height >= expectedSize.height - tolerance &&
        height <= expectedSize.height + tolerance;

      if (!isWidthInRange || !isHeightInRange) {
        showInfoToast(
          `Please upload an image close to ${expectedSize.width}x${expectedSize.height}px for "${type}". Allowed tolerance is ±${tolerance}px.`
        );
        e.target.value = null;
        return;
      }

      try {
        const imageUrl = await handleImageUpload(file, type);
        if (!imageUrl) return;

        setFormData((prev) => {
          const updates = { ...prev };
          switch (type) {
            case "logo":
              updates.header = { ...updates.header, logo: imageUrl };
              break;
            case "banner":
              updates.header = { ...updates.header, image: imageUrl };
              break;
            case "footerLogo":
              updates.footer = { ...updates.footer, logo: imageUrl };
              break;
            default:
              break;
          }
          return updates;
        });
      } catch (error) {
        console.error("Image upload failed:", error);
        showErrorToast("Failed to upload image. Please try again.");
      }
    };

    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      showErrorToast("Invalid image file.");
    };
  };

  const handleDeleteImage = async (imageUrl, type) => {
    try {
      await GLPagesService.DeleteGLPageImage(imageUrl);
      setFormData((prev) => {
        const updates = { ...prev };
        switch (type) {
          case "logo":
            updates.header.logo = "";
            break;
          case "banner":
            updates.header.image = "";
            break;
          case "footerLogo":
            updates.footer.logo = "";
            break;
          default:
            break;
        }
        return updates;
      });
    } catch (error) {
      showErrorToast("Error deleting image");
      console.error("Error deleting image:", error);
    }
  };

  const validateCurrentStep = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^[0-9]{10}$/;

    switch (currentStep) {
      case 1: // Header
        if (!formData.header.logo) {
          showErrorToast("Header logo is required.");
          return false;
        }
        if (!formData.header.image) {
          showErrorToast("Header image is required.");
          return false;
        }
        if (!formData.header.description) {
          showErrorToast("Header description is required.");
          return false;
        }
        return true;

      case 2:
        const enabledSections = Object.keys(sectionToTypeMap).filter(
          (section) => formData[section]?.enable
        );
        for (const section of enabledSections) {
          if (selectedProperties[section].length === 0) {
            showErrorToast(
              `At least one property must be selected for ${sectionToTypeMap[section]} section.`
            );
            return false;
          }
        }
        return true;

      case 3: 
        if (!formData.footer.logo) {
          showErrorToast("Footer logo is required.");
          return false;
        }
        for (const platform of [
          "facebook",
          "instagram",
          "twitter",
          "youtube",
          "linkedin",
        ]) {
          if (
            formData.footer[platform] &&
            !platformPatterns[platform].test(formData.footer[platform])
          ) {
            showErrorToast(`Invalid ${platform} URL.`);
            return false;
          }
        }
        if (!formData.contactUs.address) {
          showErrorToast("Address is required.");
          return false;
        }
        if (
          formData.contactUs.email &&
          !emailRegex.test(formData.contactUs.email)
        ) {
          showErrorToast("Invalid email format.");
          return false;
        }
        if (
          formData.contactUs.phoneNumber &&
          !phoneRegex.test(formData.contactUs.phoneNumber)
        ) {
          showErrorToast("Phone number must be a valid 10-digit number.");
          return false;
        }
        return true;

      default:
        return true;
    }
  };

  const handleSubmit = async () => {
    if (!validateCurrentStep()) return;

    if (currentStep < steps.length) {
      handleNext();
      return;
    }

    try {
      setLoading(true);

      const submitData = { ...formData };

      Object.keys(sectionToTypeMap).forEach((section) => {
        const sectionData = submitData[section];
        const selected = selectedProperties[section];

        if (
          sectionData &&
          sectionData.enable &&
          selectedProperties[section]?.length > 0
        ) {
          sectionData.propertyPages = selectedProperties[section].map(
            (prop, index) => ({
              id: prop.id || undefined,
              glPageId: prop.value,
              name: prop.label,
              order: index + 1,
            })
          );
        } else if (sectionData) {
          sectionData.propertyPages = [];
        }
      });

      const response = formData.landingPageId
        ? await GLPagesService.updateLandingGLpage(submitData)
        : await GLPagesService.postLandGLPage(submitData);

      if (response) {
        showSuccessToast(
          `GL page ${
            formData.landingPageId ? "updated" : "created"
          } successfully`
        );
        navigate(-1);
      }
    } catch (error) {
      console.error("Submit error:", error);
      showErrorToast("Failed to save GL page.");
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (!validateCurrentStep()) return;
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (!validateCurrentStep()) return;
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleStepClick = (stepNumber) => {
    if (!validateCurrentStep()) return;
    setCurrentStep(stepNumber);
  };

  const renderImageUpload = (type, label, currentImage) => (
    <div className="flex flex-col items-center w-full">
      <label
        htmlFor={`${type}-file`}
        className="flex flex-col items-center justify-center w-full h-40 border-2 border-customGreen border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100"
      >
        <div className="flex flex-col items-center justify-center pt-5 pb-6">
          <svg
            className="w-8 h-8 mb-4 text-customGreen"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 20 16"
          >
            <path
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"
            />
          </svg>
          <p className="mb-2 text-sm text-gray-500">
            <span className="font-semibold">Click to upload</span> or drag and
            drop
          </p>
          <p className="text-xs text-gray-500">
            PNG, SVG, WebP, JPG or JPEG{" "}
            {type === "logo"
              ? "100 x 35 px"
              : type === "banner"
              ? "1300 x 700 px"
              : "100 x 35 px"}
          </p>
        </div>
        <input
          id={`${type}-file`}
          type="file"
          className="hidden"
          onChange={(e) => handleImageChange(e, type)}
          accept="image/*"
        />
      </label>

      {loadingState[type] && (
        <div className="mt-4 text-center">
          <div className="spinner" />
          <p className="text-sm text-gray-600">Uploading...</p>
        </div>
      )}

      {currentImage && !loadingState[type] && (
        <div className="mt-4 relative">
          <img
            src={currentImage}
            alt={label}
            className="w-16 h-16 rounded-lg shadow-md border border-gray-200"
          />
          <button
            type="button"
            className="absolute top-0 right-0 text-[9px] w-4 h-4 text-red-500 bg-white rounded-full shadow-md"
            onClick={() => handleDeleteImage(currentImage, type)}
          >
            X
          </button>
        </div>
      )}
    </div>
  );

  const renderSectionToggle = (section) => (
    <label className="inline-flex cursor-pointer w-full items-center">
      <input
        type="checkbox"
        checked={formData[section]?.enable}
        onChange={(e) => handleSectionToggle(section, e.target.checked)}
        className="sr-only peer"
      />
      <div className="relative w-9 h-5 bg-red-500 rounded-full peer-checked:bg-emerald-600 after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-full" />
    </label>
  );

  const renderPropertySelector = (section) => {
    const selected = selectedProperties[section] || [];
    const selectedValues = selected.map((item) => item.value);
    const allOptions = sectionProperties[section] || [];

    const availableOptions = allOptions.filter(
      (option) => !selectedValues.includes(option.value)
    );

    return (
      <Select
        options={sectionProperties[section].map((option) => ({
          ...option,
          isDisabled: selectedProperties[section].some(
            (selected) => selected.value === option.value
          ),
        }))}
        value={selectedProperties[section]}
        isMulti
        onChange={(selectedOptions) => {
          setSelectedProperties((prev) => ({
            ...prev,
            [section]: selectedOptions,
          }));
        }}
      />
    );
  };

  return (
    <div className="bg-appGrayBackground min-h-screen pt-20">
      <AdminAppBar />
      <div className="px-14 pb-10 mt-4">
        <ToastContainer />
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-3">
            <BackArrows onClick={() => navigate(-1)} />
            <h1 className="text-3xl font-bold">
              {formData.landingPageId ? "Edit" : "New"} GL Page
            </h1>
          </div>
          <div className="flex gap-3">
            {formData.landingPageId && (
              <button
                onClick={() => setShowPreview(!showPreview)}
                className="bg-emerald-600 cursor-pointer text-white px-6 py-2 rounded-[10px]"
              >
                {showPreview ? "Hide Preview" : "Show Preview"}
              </button>
            )}
            <button
              onClick={handlePrevious}
              disabled={currentStep === 1}
              className={`flex items-center gap-2 px-6 py-2 rounded-[10px] ${
                currentStep === 1
                  ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                  : "bg-gray-200 hover:bg-gray-300 text-gray-700 transition-colors"
              }`}
            >
              <FaChevronLeft />
              Previous
            </button>
            <button
              onClick={currentStep === steps.length ? handleSubmit : handleNext}
              disabled={loading}
              className={`flex items-center gap-2 px-6 py-2 rounded-[10px] ${
                currentStep === steps.length
                  ? "bg-customGreen text-white"
                  : "bg-customGreen text-white"
              } transition-colors`}
            >
              {currentStep === steps.length
                ? formData.landingPageId
                  ? "Update"
                  : "Create"
                : "Next"}
              {currentStep < steps.length && <FaChevronRight />}
            </button>
          </div>
        </div>

        {showPreview && formData.landingPageId && (
          <div className="mt-[30px]">
            <iframe
              src={`http://vr-pages.glfunnel.com.s3-website-us-east-1.amazonaws.com/glpage/${formData.landingPageId}`}
              className="w-full h-[600px] border-0"
              title="Preview"
            />
          </div>
        )}

        <div className="w-full bg-white p-[17px] rounded-md mt-[30px]">
          <div className="flex items-center justify-center mt-2">
            <div className="flex items-center justify-between w-full relative">
              {steps.map((step, index) => (
                <div
                  key={step.id || index}
                  onClick={() => handleStepClick(index + 1)}
                  className="flex cursor-pointer flex-col items-center z-10 w-1/4"
                  role="button"
                  tabIndex={0}
                  aria-label={`Step ${index + 1}: ${step.label}`}
                >
                  <div
                    className={`w-8 h-8 border-2 rounded-full flex items-center justify-center font-bold mb-2 transition-all duration-500 ease-in-out transform ${
                      currentStep > index
                        ? "bg-white border-customGreen"
                        : "bg-gray-300 border-gray-300"
                    } ${currentStep === index + 1 ? "scale-110" : ""}`}
                  >
                    <div
                      className={`h-6 w-6 rounded-full flex items-center justify-center text-[16px] transition-all duration-500 ${
                        currentStep > index
                          ? "bg-customGreen text-white"
                          : "bg-gray-300 text-gray-700"
                      }`}
                    >
                      {currentStep > index ? (
                        <span className="animate-fadeIn">✓</span>
                      ) : (
                        <div className="bg-white w-6 h-6 rounded-full"></div>
                      )}
                    </div>
                  </div>
                  <span
                    className={`text-[11px] font-medium text-center transition-all duration-300 ${
                      currentStep === index + 1
                        ? "text-customGreen"
                        : "text-gray-700"
                    }`}
                  >
                    {step.label}
                  </span>
                </div>
              ))}
              <div className="absolute top-4 left-[160px] w-[75%] h-0.5 bg-gray-300 z-0">
                <div
                  className="h-0.5 bg-customGreen transition-all duration-500 ease-in-out"
                  style={{
                    width: `${((currentStep - 1) / (steps.length - 1)) * 100}%`,
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-md p-6 mt-5">
          {currentStep === 1 && (
            <>
              <Heading text="Header" Icon={MdOutlineSettings} />
              <div className="px-10 pt-6 pb-16 flex gap-8">
                {/* Left side - Image preview */}
                <div className="w-[40%]">
                  <div className="bg-white h-fit flex flex-col gap-3 rounded-[10px] p-[20px] border border-gray-200">
                    <div className="text-customGreen text-[15px] mb-2 font-semibold">
                      Section Layout Preview
                    </div>
                    <img
                      src={imageSampleGuide["GL Listing page"].headerAndHero}
                      className="w-full rounded-md"
                      alt="Header and Hero Preview"
                    />
                  </div>
                </div>

                {/* Right side - Input fields */}
                <div className="flex-1 space-y-6">
                  <div className="flex flex-col gap-1">
                    <Label label="Header Logo" isAsterisk={true} />
                    {renderImageUpload(
                      "logo",
                      "Header Logo",
                      formData.header?.logo || ""
                    )}
                  </div>
                  <div className="flex flex-col gap-1">
                    <Label label="Header Image" isAsterisk={true} />
                    {renderImageUpload(
                      "banner",
                      "Header Image",
                      formData.header?.image || ""
                    )}
                  </div>
                  <div className="flex flex-col gap-1">
                    <Label label="Description" isAsterisk={true} />
                    <input
                      value={formData.header?.description || ""}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          header: { ...prev.header, description: e.target.value },
                        }))
                      }
                      maxLength={120}
                      placeholder="Enter Description"
                      className="w-full px-4 py-2 bg-slate-100 border rounded-md"
                    />
                  </div>
                </div>
              </div>
            </>
          )}

          {currentStep === 2 && (
            <div className="space-y-8">
              <Heading text="Property Sections" Icon={MdOutlineSettings} />
              {Object.keys(sectionToTypeMap).map((section, index) => (
                <div
                  key={section}
                  className="border border-gray-200 rounded-lg overflow-hidden"
                >
                  <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-3">
                      <span className="bg-customGreen text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">
                        {index + 1}
                      </span>
                      {sectionToTypeMap[section]} Properties
                    </h3>
                  </div>

                  <div className="p-6">
                    <div className="flex gap-8 items-start">
                      {/* Left side - Image preview */}
                      <div className="w-[40%]">
                        <div className="bg-white h-fit flex flex-col gap-3 rounded-[10px] p-[20px] border border-gray-200">
                          <div className="text-customGreen text-[15px] mb-2 font-semibold">
                            Section Layout Preview
                          </div>
                          <img
                            src={
                              section === "topSellingProperties"
                                ? imageSampleGuide["GL Listing page"]
                                    .topSellingProperties
                                : section === "plotProperties"
                                ? imageSampleGuide["GL Listing page"]
                                    .plotProperties
                                : section === "villaProperties"
                                ? imageSampleGuide["GL Listing page"]
                                    .villaProperties
                                : section === "apartmentProperties"
                                ? imageSampleGuide["GL Listing page"]
                                    .apartmentProperties
                                : imageSampleGuide["GL Listing page"]
                                    .plotProperties
                            }
                            className="w-full rounded-md"
                            alt={`${sectionToTypeMap[section]} Preview`}
                          />
                        </div>
                      </div>

                      {/* Right side - Controls */}
                      <div className="flex-1 space-y-6">
                        <div className="flex items-center justify-between">
                          <div className="flex flex-col">
                            <Label label="Enable Section" />
                            <span className="text-xs text-gray-500 mt-1">
                              Toggle to enable/disable this property section
                            </span>
                          </div>
                          {renderSectionToggle(section)}
                        </div>

                        {formData[section]?.enable && (
                          <div className="space-y-4 border-t border-gray-100 pt-6">
                            <div className="flex flex-col">
                              <Label
                                label="Select Properties"
                                isAsterisk={true}
                              />
                              <span className="text-xs text-gray-500 mt-1 mb-3">
                                Choose properties to display in this section
                              </span>
                              {renderPropertySelector(section)}
                            </div>

                            {selectedProperties[section]?.length > 0 && (
                              <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200">
                                <div className="text-sm font-medium text-green-800 mb-2">
                                  Selected Properties (
                                  {selectedProperties[section]?.length})
                                </div>
                                <div className="flex flex-wrap gap-2">
                                  {selectedProperties[section]?.map(
                                    (prop, idx) => (
                                      <span
                                        key={prop.value}
                                        className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800"
                                      >
                                        {idx + 1}. {prop.label}
                                      </span>
                                    )
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {!formData[section]?.enable && (
                          <div className="text-center py-12 text-gray-400">
                            <div className="mb-4 flex justify-center">
                              <svg
                                className="w-16 h-16 text-gray-300"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="1.5"
                                  d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
                                />
                              </svg>
                            </div>
                            <p className="text-sm font-medium text-gray-500 mb-1">
                              Section Disabled
                            </p>
                            <p className="text-xs text-gray-400">
                              Enable this section to configure properties
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {currentStep === 3 && (
            <>
              <Heading text="Footer & Contact" Icon={MdOutlineSettings} />
              <div className="px-10 pt-6 pb-16">
                <div className="flex gap-8">
                  {/* Left side - Image preview */}
                  <div className="w-[40%]">
                    <div className="bg-white h-fit flex flex-col gap-3 rounded-[10px] p-[20px] border border-gray-200">
                      <div className="text-customGreen text-[15px] mb-2 font-semibold">
                        Section Layout Preview
                      </div>
                      <img
                        src={imageSampleGuide["GL Listing page"].footerLogo}
                        className="w-full rounded-md"
                        alt="Footer Preview"
                      />
                    </div>
                  </div>

                  {/* Right side - Input fields in two columns */}
                  <div className="flex-1 flex-col flex gap-6">
                    {/* Footer Settings Column */}
                    <div className="flex-1 space-y-6">
                      <div className="mb-4">
                        <h4 className="text-lg font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-200">
                          Footer Settings
                        </h4>
                      </div>
                      
                      <div className="flex flex-col gap-1">
                        <Label label="Footer Logo" isAsterisk={true} />
                        {renderImageUpload(
                          "footerLogo",
                          "Footer Logo",
                          formData?.footer?.logo || ""
                        )}
                      </div>
                      
                      {[
                        "facebook",
                        "instagram",
                        "twitter",
                        "youtube",
                        "linkedin",
                      ].map((platform) => (
                        <div key={platform} className="flex flex-col gap-1">
                          <div className="flex flex-col">
                            <Label
                              label={`${
                                platform.charAt(0).toUpperCase() + platform.slice(1)
                              } Link`}
                            />
                            {linkErrors[platform] && (
                              <div className="text-red-500 text-sm">
                                Please enter a valid {platform} URL starting with
                                http:// or https://
                              </div>
                            )}
                          </div>
                          <input
                            value={formData?.footer?.[platform] || ""}
                            onChange={(e) => {
                              const value = e.target.value;
                              const pattern = platformPatterns[platform];

                              if (value === "" || pattern.test(value)) {
                                setFormData((prev) => ({
                                  ...prev,
                                  footer: { ...prev.footer, [platform]: value },
                                }));
                                setLinkErrors((prev) => ({
                                  ...prev,
                                  [platform]: value && !pattern.test(value),
                                }));
                              } else {
                                setLinkErrors((prev) => ({
                                  ...prev,
                                  [platform]: true,
                                }));
                              }
                            }}
                            placeholder={`Enter ${platform} link`}
                            className="w-full px-4 py-2 bg-slate-100 border rounded-md focus:ring-2 focus:ring-customGreen focus:border-customGreen transition-colors"
                          />
                        </div>
                      ))}
                    </div>

                    {/* Contact Information Column */}
                    <div className="flex-1 space-y-6">
                      <div className="mb-4">
                        <h4 className="text-lg font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-200">
                          Contact Information
                        </h4>
                      </div>
                      
                      {["address", "email", "phoneNumber"].map((field) => (
                        <div key={field} className="flex flex-col gap-1">
                          <Label
                            label={
                              field === "phoneNumber" 
                                ? "Phone Number" 
                                : field.charAt(0).toUpperCase() + field.slice(1)
                            }
                            isAsterisk={field === "address"}
                          />
                          
                          {field === "address" ? (
                            <textarea
                              value={formData?.contactUs?.[field] || ""}
                              onChange={(e) => {
                                const value = e.target.value;
                                if (value.length > 120) return;

                                setFormData((prev) => ({
                                  ...prev,
                                  contactUs: {
                                    ...prev.contactUs,
                                    [field]: value,
                                  },
                                }));
                              }}
                              placeholder="Enter Address"
                              rows={3}
                              className="w-full px-4 py-2 bg-slate-100 border rounded-md focus:ring-2 focus:ring-customGreen focus:border-customGreen transition-colors resize-none"
                            />
                          ) : (
                            <input
                              value={formData?.contactUs?.[field] || ""}
                              onChange={(e) => {
                                const value = e.target.value;

                                if (field === "email") {
                                  // Allow any input for email, validation happens on blur/submit
                                } else if (field === "phoneNumber") {
                                  if (/[^0-9]/.test(value)) return;
                                  if (value.length > 10) return;
                                }

                                setFormData((prev) => ({
                                  ...prev,
                                  contactUs: {
                                    ...prev.contactUs,
                                    [field]: value,
                                  },
                                }));
                              }}
                              placeholder={`Enter ${
                                field === "phoneNumber" 
                                  ? "Phone Number" 
                                  : field.charAt(0).toUpperCase() + field.slice(1)
                              }`}
                              type={field === "email" ? "email" : "text"}
                              className="w-full px-4 py-2 bg-slate-100 border rounded-md focus:ring-2 focus:ring-customGreen focus:border-customGreen transition-colors"
                            />
                          )}
                          
                          {/* Validation Messages */}
                          {field === "address" &&
                            formData?.contactUs?.[field]?.length > 120 && (
                              <p className="text-red-500 text-sm mt-1">
                                Address can't exceed 120 characters.
                              </p>
                            )}
                          {field === "email" &&
                            formData?.contactUs?.[field] &&
                            !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
                              formData?.contactUs?.[field]
                            ) && (
                              <p className="text-red-500 text-sm mt-1">
                                Invalid email format.
                              </p>
                            )}
                          {field === "phoneNumber" &&
                            formData?.contactUs?.[field] &&
                            formData?.contactUs?.[field]?.length !== 10 && (
                              <p className="text-orange-500 text-sm mt-1">
                                Phone number should be 10 digits.
                              </p>
                            )}
                          
                          {/* Character count for address */}
                          {field === "address" && (
                            <p className="text-gray-500 text-xs mt-1">
                              {formData?.contactUs?.[field]?.length || 0}/120 characters
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          
        </div>

        <div className="flex justify-between pb-20 px-6 mb-10 mt-6">
          <button
            onClick={handlePrevious}
            disabled={currentStep === 1}
            className={`flex items-center gap-2 px-6 py-2 rounded-md ${
              currentStep === 1
                ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                : "bg-gray-200 hover:bg-gray-300 text-gray-700 transition-colors"
            }`}
          >
            <FaChevronLeft />
            Previous
          </button>
          <button
            onClick={currentStep === steps.length ? handleSubmit : handleNext}
            disabled={loading}
            className={`flex items-center gap-2 px-6 py-2 rounded-md ${
              currentStep === steps.length
                ? "bg-customGreen text-white"
                : "bg-customGreen text-white"
            } transition-colors`}
          >
            {currentStep === steps.length
              ? formData.landingPageId
                ? "Update"
                : "Create"
              : "Next"}
            {currentStep < steps.length && <FaChevronRight />}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GLpagesCreateTemplate;